<template>
  <div class="archive-files-page">
    <div v-loading="loading" class="page-content">
      <!-- 基本信息 -->
      <div class="info-section">
        <h3 class="section-title">基本信息</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">课程：</span>
            <span class="value">{{ groupInfo.courseName || '-' }}</span>
          </div>
          <br/>
          <div class="info-item">
            <span class="label">小组编号：</span>
            <span class="value">{{ groupInfo.groupNo || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">小组名称：</span>
            <span class="value">{{ groupInfo.groupName || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">归档时间：</span>
            <span class="value">{{ groupInfo.archiveSubmitDate || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">归档状态：</span>
            <span class="value" :class="{'status-archived': groupInfo.archiveStatusCode === '3'}">
              {{ groupInfo.archiveStatusName || '-' }}
            </span>
          </div>
        </div>
      </div>

      <!-- 小组文件 -->
      <div class="files-section">
        <h3 class="section-title">小组文件</h3>
        <div class="file-list">
          <div v-for="file in groupFiles" :key="file.fieldName" class="file-item">
            <div class="file-name">{{ file.name }}</div>
            <div class="file-actions">
              <el-button v-if="file.hasFile"
                         type="text"
                         size="small"
                         @click="downloadFile(file.url)">
                下载
              </el-button>
              <template v-if="canReplace">
                <span v-if="file.hasFile" class="divider">|</span>
                <el-button type="text"
                           size="small"
                           @click="openUploadDialog(file.fieldName, file.name, 'group')">
                  替换
                </el-button>
              </template>
              <span v-if="!file.hasFile" class="no-file-tip">（暂无文件）</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 组员列表 -->
      <div class="members-section">
        <h3 class="section-title">组员列表</h3>
        <el-table :data="members" border stripe>
          <el-table-column prop="studentNo" label="学号" width="150" align="center"/>
          <el-table-column prop="trueName" label="姓名" width="120" align="center"/>
          <el-table-column label="是否组长" width="100" align="center">
            <template slot-scope="scope">
              <span :class="{'leader-yes': scope.row.isLeader === '是'}">
                {{ scope.row.isLeader }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="个人心得" width="150" align="center">
            <template slot-scope="scope">
              <div class="file-actions">
                <el-button v-if="scope.row.hasFile"
                           type="text"
                           size="small"
                           @click="downloadFile(scope.row.personalSummaryFileUrl)">
                  下载
                </el-button>
                <span v-else class="no-file-tip">暂无</span>
                <template v-if="canReplace">
                  <span class="divider">|</span>
                  <el-button type="text"
                             size="small"
                             @click="openUploadDialog(scope.row.memberId, scope.row.trueName + '的个人心得', 'member')">
                    替换
                  </el-button>
                </template>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- 文件上传弹框 -->
    <el-dialog :title="uploadDialog.title"
               :visible.sync="uploadDialog.visible"
               width="500px"
               :close-on-click-modal="false"
               :append-to-body="true">
      <div class="upload-dialog-content">
        <el-form label-width="80px" size="mini">
          <el-form-item label="选择文件">
            <el-upload
              ref="upload"
              :auto-upload="false"
              :show-file-list="false"
              :on-change="handleFileChange"
              action="#">
              <el-button size="small" type="primary">选择文件</el-button>
            </el-upload>
            <div v-if="uploadDialog.fileName" class="selected-file">
              <i class="el-icon-document"></i>
              <span>{{ uploadDialog.fileName }}</span>
              <i class="el-icon-close" @click="clearFile"></i>
            </div>
          </el-form-item>
        </el-form>
        <div class="upload-tip">
          支持的文件格式：doc、docx、ppt、pptx、xls、xlsx、pdf、zip、rar<br>
          文件大小不超过100MB
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="uploadDialog.visible = false">取消</el-button>
        <el-button size="small"
                   type="primary"
                   :loading="uploadDialog.uploading"
                   :disabled="!uploadDialog.file"
                   @click="handleUploadSubmit">提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ArchiveFilesPage',
  props: {
    extraParams: Object,
    visible: Boolean
  },
  data () {
    return {
      loading: false,
      groupInfo: {},
      groupFiles: [],
      members: [],
      uploadUrl: '/workspace/manager/portal/upload/file',
      uploadDialog: {
        visible: false,
        title: '',
        file: null,
        fileName: '',
        uploading: false,
        targetId: '', // 文件字段名或组员ID
        targetName: '', // 文件名称描述
        type: '' // 'group' 或 'member'
      }
    }
  },
  computed: {
    // 是否可以替换文件（只有已归档状态才能替换）
    canReplace () {
      return this.groupInfo.archiveStatusCode === '3'
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.loadData()
      }
    }
  },
  mounted () {
    if (this.visible) {
      this.loadData()
    }
  },
  methods: {
    // 加载数据
    loadData () {
      const groupId = this.$attrs['extra-id']
      if (!groupId) {
        this.$message.warning('未获取到小组ID')
        return
      }

      this.loading = true
      this.$ajax({
        url: '/workspace/manager/training/archive/getFilesData',
        method: 'post',
        data: this.$qs.stringify({groupId: groupId})
      }).then(response => {
        if (response.data && response.data.responseCode === 'SUCCESS') {
          const data = response.data.data
          this.groupInfo = data.groupInfo || {}
          this.groupFiles = data.groupFiles || []
          this.members = data.members || []
        } else {
          this.$message.warning(response.data.message || '加载失败')
        }
      }, () => {
        this.$message.error('请求发送失败，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },

    // 打开上传弹框
    openUploadDialog (targetId, targetName, type) {
      this.uploadDialog = {
        visible: true,
        title: '替换文件 - ' + targetName,
        file: null,
        fileName: '',
        uploading: false,
        targetId: targetId,
        targetName: targetName,
        type: type
      }
    },

    // 文件选择改变
    handleFileChange (file) {
      // 文件大小校验
      const maxSize = 100 * 1024 * 1024 // 100MB
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过100MB')
        return
      }

      // 文件类型校验
      const allowedTypes = ['.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.pdf', '.zip', '.rar']
      const fileName = file.name
      const suffix = fileName.substring(fileName.lastIndexOf('.')).toLowerCase()
      if (!allowedTypes.includes(suffix)) {
        this.$message.error('不支持的文件格式，请上传doc、docx、ppt、pptx、xls、xlsx、pdf、zip、rar格式的文件')
        return
      }

      this.uploadDialog.file = file.raw
      this.uploadDialog.fileName = fileName
    },

    // 清除选择的文件
    clearFile () {
      this.uploadDialog.file = null
      this.uploadDialog.fileName = ''
    },

    // 提交上传
    handleUploadSubmit () {
      if (!this.uploadDialog.file) {
        this.$message.warning('请先选择文件')
        return
      }

      this.uploadDialog.uploading = true

      // 创建FormData
      const formData = new FormData()
      formData.append('uploadFile', this.uploadDialog.file)

      // 上传文件
      this.$ajax({
        url: this.uploadUrl,
        method: 'post',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(response => {
        if (response.data && response.data.responseCode === 'SUCCESS') {
          const uploadLink = response.data.uploadLink
          // 调用替换接口
          this.replaceFile(uploadLink)
        } else {
          this.$message.error(response.data.message || '文件上传失败')
          this.uploadDialog.uploading = false
        }
      }, () => {
        this.$message.error('文件上传失败，请稍后重试')
        this.uploadDialog.uploading = false
      })
    },

    // 替换文件
    replaceFile (fileUrl) {
      const groupId = this.$attrs['extra-id']
      const type = this.uploadDialog.type
      const targetId = this.uploadDialog.targetId

      if (type === 'group') {
        // 替换小组文件
        this.$ajax({
          url: '/workspace/manager/training/archive/replaceGroupFile',
          method: 'post',
          data: this.$qs.stringify({
            groupId: groupId,
            fieldName: targetId,
            fileUrl: fileUrl
          })
        }).then(response => {
          if (response.data && response.data.responseCode === 'SUCCESS') {
            this.$message.success('文件替换成功')
            this.uploadDialog.visible = false
            this.loadData()
          } else {
            this.$message.warning(response.data.message || '替换失败')
          }
        }, () => {
          this.$message.error('请求发送失败，请稍后重试')
        }).finally(() => {
          this.uploadDialog.uploading = false
        })
      } else if (type === 'member') {
        // 替换组员个人心得
        this.$ajax({
          url: '/workspace/manager/training/archive/replaceMemberFile',
          method: 'post',
          data: this.$qs.stringify({
            memberId: targetId,
            fileUrl: fileUrl
          })
        }).then(response => {
          if (response.data && response.data.responseCode === 'SUCCESS') {
            this.$message.success('文件替换成功')
            this.uploadDialog.visible = false
            this.loadData()
          } else {
            this.$message.warning(response.data.message || '替换失败')
          }
        }, () => {
          this.$message.error('请求发送失败，请稍后重试')
        }).finally(() => {
          this.uploadDialog.uploading = false
        })
      }
    },

    // 下载文件
    downloadFile (url) {
      if (!url) {
        this.$message.warning('文件不存在')
        return
      }
      window.open(url, '_blank')
    }
  }
}
</script>

<style scoped lang="scss">
.archive-files-page {
  padding: 20px;
  height: 100%;
  overflow-y: auto;

  .page-content {
    min-height: 100%;
  }

  .section-title {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #409EFF;
  }

  .info-section {
    margin-bottom: 30px;

    .info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 15px;

      .info-item {
        display: flex;
        align-items: center;

        .label {
          font-weight: bold;
          color: #606266;
          margin-right: 10px;
        }

        .value {
          color: #303133;

          &.status-archived {
            color: #67c23a;
            font-weight: bold;
          }
        }
      }
    }
  }

  .files-section {
    margin-bottom: 30px;

    .file-list {
      .file-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 15px;
        border: 1px solid #EBEEF5;
        border-radius: 4px;
        margin-bottom: 10px;
        background-color: #F5F7FA;

        .file-name {
          font-weight: 500;
          color: #303133;
        }

        .file-actions {
          display: flex;
          align-items: center;
          gap: 5px;

          .divider {
            color: #DCDFE6;
            margin: 0 5px;
          }

          .no-file-tip {
            color: #909399;
            font-size: 12px;
            margin-left: 10px;
          }
        }
      }
    }
  }

  .members-section {
    .leader-yes {
      color: #67c23a;
      font-weight: bold;
    }

    .file-actions {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;

      .no-file-tip {
        color: #909399;
        font-size: 12px;
      }

      .divider {
        color: #DCDFE6;
        margin: 0 5px;
      }
    }
  }
}
</style>

<style lang="scss">
// 弹框样式（非scoped，因为弹框append-to-body）
.upload-dialog-content {
  padding: 20px 40px;

  .selected-file {
    margin-top: 10px;
    padding: 8px 12px;
    background-color: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 8px;

    .el-icon-document {
      color: #409eff;
      font-size: 16px;
    }

    span {
      flex: 1;
      color: #606266;
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .el-icon-close {
      color: #909399;
      cursor: pointer;
      font-size: 14px;

      &:hover {
        color: #f56c6c;
      }
    }
  }

  .upload-tip {
    margin-top: 15px;
    background-color: #fafafa;
    color: #909399;
    font-size: 12px;
    line-height: 1.6;
  }
}
</style>
